import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { Sparkline } from '../components/Charts';
import UpgradeButton from '../components/UpgradeButton';
import AppFooter from '../components/AppFooter';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import {
  Bell, Warning, CalendarBlank, Fire, ArrowRight, Circle, CheckCircle,
  Lightning, At, ChatCircleText, ArrowsDownUp, UserPlus, Plus, FolderOpen,
  Eye, LinkSimple, ChartLineUp, Sparkle, ListChecks, ArrowUpRight,
} from '@phosphor-icons/react';

const ACTIVITY_ICON = {
  created: Plus, updated: ArrowsDownUp, commented: ChatCircleText,
  mentioned: At, assigned: UserPlus, status_changed: ArrowsDownUp,
};
const ACTIVITY_COLOR = {
  created: 'bg-blue-50 text-blue-600',
  updated: 'bg-zinc-100 text-zinc-600',
  commented: 'bg-violet-50 text-violet-600',
  mentioned: 'bg-amber-50 text-amber-600',
  assigned: 'bg-emerald-50 text-emerald-600',
  status_changed: 'bg-amber-50 text-amber-600',
};

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}
function daysBetween(a, b) {
  return Math.ceil((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24));
}

// At-a-glance stat tile for the Risk Radar strip.
function StatTile({ icon: Icon, label, value, color, accent = false, onClick, testid, sub }) {
  // Enterprise treatment — clean white surface, 1px border, a single semantic
  // left-accent bar and a soft icon chip. Neutral dark figures; colour is a
  // restrained signal, not a gradient. Alert tiles (accent) tint the number.
  const palette = {
    red:     { bar: 'bg-rose-500',    chip: 'bg-rose-50 text-rose-600 ring-rose-100',       num: 'text-rose-600' },
    amber:   { bar: 'bg-amber-500',   chip: 'bg-amber-50 text-amber-600 ring-amber-100',    num: 'text-amber-700' },
    blue:    { bar: 'bg-indigo-500',  chip: 'bg-indigo-50 text-indigo-600 ring-indigo-100', num: 'text-indigo-700' },
    emerald: { bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-600 ring-emerald-100', num: 'text-emerald-700' },
    violet:  { bar: 'bg-violet-500',  chip: 'bg-violet-50 text-violet-600 ring-violet-100',  num: 'text-violet-700' },
    zinc:    { bar: 'bg-slate-400',   chip: 'bg-zinc-100 text-zinc-600 ring-zinc-200',       num: 'text-zinc-900' },
  }[color] || { bar: 'bg-slate-400', chip: 'bg-zinc-100 text-zinc-600 ring-zinc-200', num: 'text-zinc-900' };

  const Wrap = onClick ? 'button' : 'div';
  return (
    <Wrap
      onClick={onClick}
      data-testid={testid}
      className={`relative overflow-hidden text-left rounded-xl p-4 pl-5 group transition-all duration-200
        bg-white ring-1 ${accent ? 'ring-zinc-300 shadow-sm' : 'ring-zinc-200'}
        ${onClick ? 'hover:-translate-y-0.5 hover:shadow-md cursor-pointer' : ''}`}
    >
      <span className={`absolute left-0 top-3.5 bottom-3.5 w-[3px] rounded-full ${palette.bar}`} />
      <div className="relative flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg ring-1 flex items-center justify-center transition-transform group-hover:scale-105 ${palette.chip}`}>
          <Icon size={16} weight="bold" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-400">{label}</p>
          <div className="flex items-baseline gap-2">
            <p className={`font-['Outfit'] text-[24px] font-black tracking-tight tabular-nums ${accent ? palette.num : 'text-zinc-900'}`}>{value}</p>
            {sub && <p className="text-[10px] truncate text-zinc-400 font-medium">{sub}</p>}
          </div>
        </div>
        {onClick && <ArrowUpRight size={14} className="text-zinc-300 group-hover:text-zinc-500 flex-shrink-0 transition-colors" />}
      </div>
    </Wrap>
  );
}

function FocusBucket({ title, tasks, icon: Icon, tone, navigate, emptyCopy, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-200/70 overflow-hidden flex flex-col" data-testid={testid || `focus-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${tone.bg} ${tone.text}`}>
            <Icon size={14} weight="bold" />
          </div>
          <h3 className="text-[12px] font-bold text-zinc-800">{title}</h3>
        </div>
        <span className={`text-[12px] font-black tabular-nums ${tone.text}`}>{tasks.length}</span>
      </div>
      <div className="divide-y divide-zinc-50 flex-1">
        {tasks.length === 0 && <p className="text-center text-[11px] text-zinc-400 py-6 italic">{emptyCopy}</p>}
        {tasks.slice(0, 5).map(t => (
          <button key={t.task_id} data-testid={`focus-task-${t.task_id}`} onClick={() => navigate(`/tasks/${t.task_id}`)}
            className="w-full flex items-center gap-2 px-4 py-2 hover:bg-zinc-50/80 text-left transition-colors group">
            <span className="text-[10px] font-mono text-zinc-400 w-12 flex-shrink-0">{t.task_key}</span>
            <span className="flex-1 text-[12px] text-zinc-700 truncate group-hover:text-blue-700 transition-colors">{t.title}</span>
            {t.due_date && <span className="text-[10px] text-zinc-400 flex-shrink-0">{new Date(t.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, currentOrg, getOrgHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [focus, setFocus] = useState({ overdue: [], due_today: [], upcoming: [] });
  const [activity, setActivity] = useState([]);
  const [activeSprints, setActiveSprints] = useState([]);
  const [risk, setRisk] = useState(null);
  const [throughputSpark, setThroughputSpark] = useState([]);

  const load = useCallback(async () => {
    if (!currentOrg) return;
    const h = { withCredentials: true, headers: getOrgHeaders() };
    try {
      const [s, unr, f, act, sp, r, thr] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, h),
        axios.get(`${API}/notifications/unread-count`, h),
        axios.get(`${API}/dashboard/my-focus`, h),
        axios.get(`${API}/dashboard/activity-feed?limit=15`, h),
        axios.get(`${API}/dashboard/active-sprints`, h),
        axios.get(`${API}/dashboard/risk-radar`, h),
        axios.get(`${API}/insights/throughput?weeks=8`, h).catch(() => ({ data: { weeks: [] } })),
      ]);
      setStats(s.data);
      setUnreadCount(unr.data?.count || 0);
      setFocus(f.data); setActivity(act.data); setActiveSprints(sp.data);
      setRisk(r.data);
      setThroughputSpark((thr.data?.weeks || []).map(w => w.completed || 0));
    } catch (e) { console.error(e); }
  }, [API, currentOrg, getOrgHeaders]);

  useEffect(() => { load(); }, [load]);

  const greet = () => {
    const h = new Date().getHours();
    return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
  };
  const dayName = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const completionPct = stats?.total_tasks > 0 ? Math.round((stats.done / stats.total_tasks) * 100) : 0;

  return (
    <div className="flex-1 overflow-auto bg-gradient-to-b from-zinc-50/60 to-white" data-testid="dashboard-page">
      {/* Hero header — gradient backdrop with decorative blob */}
      <div className="relative bg-gradient-to-br from-blue-700 via-indigo-700 to-violet-700 px-8 py-10 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.14),transparent_55%)]" />
        <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute right-10 top-6 w-40 h-40 rounded-full bg-violet-300/10 blur-2xl" />
        <div className="relative max-w-[1400px] mx-auto">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-blue-200 font-bold mb-1.5 inline-flex items-center gap-1.5">
                <Sparkle size={11} weight="fill" /> {currentOrg?.org_name}
              </p>
              <h1 className="font-['Outfit'] text-[30px] font-black tracking-tight leading-tight">
                Good {greet()}, {user?.name?.split(' ')[0] || 'there'}
              </h1>
              <p className="text-[13px] text-blue-100/90 mt-1.5">
                {dayName}{' · '}
                {unreadCount > 0 ? (
                  <span className="inline-flex items-center gap-1" data-testid="home-unread-pill">
                    <Bell size={12} weight="fill" /> <strong className="text-white font-bold">{unreadCount}</strong> unread
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1"><Bell size={12} /> all caught up</span>
                )}
              </p>
            </div>
            <div className="flex items-start gap-3">
              <UpgradeButton />
              {throughputSpark.some(v => v > 0) && (
                <div className="bg-white/10 backdrop-blur rounded-xl px-4 py-3 border border-white/15 min-w-[180px]" data-testid="hero-velocity-card">
                  <p className="text-[10px] uppercase tracking-wider text-blue-200 font-bold mb-1 inline-flex items-center gap-1">
                    <ChartLineUp size={11} /> Last 8 weeks
                  </p>
                  <p className="font-['Outfit'] text-[20px] font-black text-white tabular-nums leading-none">
                    {throughputSpark.reduce((a, b) => a + b, 0)} <span className="text-[11px] font-medium text-blue-200">tasks shipped</span>
                  </p>
                  <div className="mt-2"><Sparkline values={throughputSpark} color="#a5b4fc" height={28} fillOpacity={0.3} /></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-8 pt-8 pb-6 max-w-[1400px] mx-auto space-y-5">
        {/* Risk Radar — 5 stat tiles giving instant 'state of you' */}
        {risk && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="home-risk-radar">
            <StatTile icon={Warning} label="Overdue" value={risk.overdue_count} color="red"
              accent={risk.overdue_count > 0} sub="assigned to you"
              onClick={() => navigate('/my-tasks')} testid="radar-overdue" />
            <StatTile icon={Fire} label="Due today" value={risk.due_today_count} color="amber"
              sub="don't slip" onClick={() => navigate('/my-tasks')} testid="radar-due-today" />
            <StatTile icon={LinkSimple} label="Blocked" value={risk.blocked_count} color="violet"
              accent={risk.blocked_count > 0} sub="by linked deps"
              onClick={risk.blocked_count > 0 ? () => navigate('/my-tasks') : undefined} testid="radar-blocked" />
            <StatTile icon={Eye} label="Watching" value={risk.watching_count} color="blue"
              sub="subscribed tasks" testid="radar-watching" />
            <StatTile icon={CheckCircle} label="Done this week" value={risk.done_this_week} color="emerald"
              sub="keep it up" testid="radar-done-week" />
          </div>
        )}

        {/* Focus row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FocusBucket title="Overdue" tasks={focus.overdue} icon={Warning} navigate={navigate}
            tone={{ bg: 'bg-red-50', text: 'text-red-600' }} emptyCopy="Nothing overdue — nice!" />
          <FocusBucket title="Due today" tasks={focus.due_today} icon={Fire} navigate={navigate}
            tone={{ bg: 'bg-amber-50', text: 'text-amber-600' }} emptyCopy="Nothing due today" />
          <FocusBucket title="Next 7 days" tasks={focus.upcoming} icon={CalendarBlank} navigate={navigate}
            tone={{ bg: 'bg-blue-50', text: 'text-blue-600' }} emptyCopy="Nothing on the horizon" />
        </div>

        {/* Team completion */}
        {stats && stats.total_tasks > 0 && (
          <div className="bg-white rounded-2xl border border-zinc-200/70 p-5">
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><ListChecks size={14} weight="bold" /></div>
                <span className="text-[13px] font-bold text-zinc-800">Team completion</span>
              </div>
              <span className="text-[12px] text-zinc-500 tabular-nums">{stats.done} / {stats.total_tasks} done · <strong className="text-zinc-800">{completionPct}%</strong></span>
            </div>
            <Progress value={completionPct} className="h-2.5" data-testid="team-completion-bar" />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Watching tasks (left column on top) + Active sprints */}
          <div className="lg:col-span-1 space-y-5">
            {risk?.watching?.length > 0 && (
              <div className="bg-white rounded-2xl border border-zinc-200/70 overflow-hidden" data-testid="home-watching-list">
                <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
                  <h3 className="text-[13px] font-bold text-zinc-800 flex items-center gap-1.5"><Eye size={14} className="text-blue-600" weight="bold" /> Watching</h3>
                  <span className="text-[10px] text-zinc-400 tabular-nums">{risk.watching.length}</span>
                </div>
                <div className="divide-y divide-zinc-50">
                  {risk.watching.map(t => (
                    <button key={t.task_id} onClick={() => navigate(`/tasks/${t.task_id}`)}
                      className="w-full text-left px-5 py-2.5 hover:bg-zinc-50/80 transition-colors group" data-testid={`watching-${t.task_id}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-zinc-400 flex-shrink-0">{t.task_key}</span>
                        <span className="text-[12px] text-zinc-700 truncate flex-1 group-hover:text-blue-700">{t.title}</span>
                      </div>
                      <p className="text-[10px] text-zinc-400 mt-0.5">Updated {timeAgo(t.updated_at)}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="bg-white rounded-2xl border border-zinc-200/70 overflow-hidden" data-testid="home-active-sprints">
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
                <h3 className="text-[13px] font-bold text-zinc-800 flex items-center gap-1.5"><Lightning size={14} weight="bold" className="text-amber-500" /> Active sprints</h3>
                <span className="text-[10px] text-zinc-400 tabular-nums">
                  {activeSprints.length} sprint{activeSprints.length !== 1 ? 's' : ''} · {new Set(activeSprints.map(s => s.project_id)).size} project{new Set(activeSprints.map(s => s.project_id)).size !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="max-h-[480px] overflow-y-auto">
                {activeSprints.length === 0 && <p className="px-5 py-8 text-[12px] text-zinc-400 text-center italic">No active sprints across your projects</p>}
                {/* Group sprints by project so it's instantly clear which project each belongs to. */}
                {(() => {
                  const groups = {};
                  for (const s of activeSprints) {
                    const k = s.project_id || 'none';
                    if (!groups[k]) groups[k] = { project: s.project, sprints: [] };
                    groups[k].sprints.push(s);
                  }
                  return Object.values(groups).map(group => (
                    <div key={group.project?.project_id || 'none'} data-testid={`sprint-group-${group.project?.project_id}`}>
                      <button
                        onClick={() => group.project?.project_id && navigate(`/projects/${group.project.project_id}`)}
                        className="w-full px-5 py-2 bg-zinc-50/60 border-y border-zinc-100 sticky top-0 z-[1] backdrop-blur hover:bg-zinc-100/80 transition-colors text-left flex items-center justify-between group"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-[10px] text-zinc-500 px-1.5 py-0.5 rounded bg-white border border-zinc-200 font-bold flex-shrink-0">{group.project?.key || '—'}</span>
                          <span className="text-[11px] font-bold text-zinc-700 truncate group-hover:text-blue-700">{group.project?.name || 'Unknown project'}</span>
                        </div>
                        <span className="text-[10px] text-zinc-400 tabular-nums flex-shrink-0">{group.sprints.length}</span>
                      </button>
                      {group.sprints.map(s => {
                        const pct = s.task_count > 0 ? Math.round((s.done_count / s.task_count) * 100) : 0;
                        const daysLeft = s.end_date ? daysBetween(new Date(), s.end_date) : null;
                        const tone = daysLeft != null && daysLeft < 2 ? 'text-red-600' : daysLeft != null && daysLeft < 5 ? 'text-amber-600' : 'text-zinc-500';
                        return (
                          <button key={s.sprint_id} onClick={() => navigate(`/projects/${s.project_id}`)} data-testid={`home-sprint-${s.sprint_id}`}
                            className="w-full px-5 py-2.5 text-left hover:bg-zinc-50 transition-colors border-b border-zinc-50 last:border-0">
                            <div className="flex items-center justify-between mb-1.5">
                              <p className="text-[12px] font-semibold text-zinc-800 truncate flex-1 mr-2">{s.name}</p>
                              <span className={`text-[10px] font-bold tabular-nums flex-shrink-0 ${tone}`}>
                                {daysLeft != null ? (daysLeft > 0 ? `${daysLeft}d left` : daysLeft === 0 ? 'last day' : 'ended') : ''}
                              </span>
                            </div>
                            <Progress value={pct} className="h-1.5" />
                            <p className="text-[10px] text-zinc-400 mt-1 tabular-nums">{s.done_count} / {s.task_count} · {pct}%</p>
                          </button>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* Recent activity feed */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-zinc-200/70 overflow-hidden" data-testid="home-activity-feed">
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
                <h3 className="text-[13px] font-bold text-zinc-800 flex items-center gap-1.5"><ChatCircleText size={14} weight="bold" className="text-violet-600" /> Recent activity</h3>
                <button onClick={load} className="text-[11px] text-blue-600 hover:underline font-medium">Refresh</button>
              </div>
              <div className="divide-y divide-zinc-50 max-h-[680px] overflow-y-auto">
                {activity.length === 0 && <p className="px-5 py-10 text-[12px] text-zinc-400 text-center italic">No recent activity</p>}
                {activity.map(a => {
                  const Icon = ACTIVITY_ICON[a.action] || Circle;
                  const colorClass = ACTIVITY_COLOR[a.action] || 'bg-zinc-100 text-zinc-600';
                  return (
                    <button key={a.activity_id} data-testid={`activity-${a.activity_id}`}
                      onClick={() => a.task_id && navigate(`/tasks/${a.task_id}`)}
                      className="w-full flex items-start gap-3 px-5 py-3 hover:bg-zinc-50/80 text-left transition-colors group">
                      <div className="relative flex-shrink-0 mt-0.5">
                        <Avatar className="h-8 w-8">
                          {a.user?.avatar && <AvatarImage src={a.user.avatar} />}
                          <AvatarFallback className={`${getAvatarStyle(a.user?.user_id || a.user?.name).bg} text-white text-[9px] font-bold`}>{getInitials(a.user?.name)}</AvatarFallback>
                        </Avatar>
                        <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white ${colorClass}`}>
                          <Icon size={9} weight="bold" />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] text-zinc-700 leading-snug">
                          <span className="font-semibold text-zinc-800">{a.user?.name || 'Someone'}</span>{' '}
                          <span className="text-zinc-500">{a.action?.replace(/_/g, ' ')}</span>{' '}
                          {a.task && <span className="font-mono font-bold text-blue-700 group-hover:underline">{a.task.task_key}</span>}
                          {a.task?.title && <span className="text-zinc-600"> — {a.task.title}</span>}
                        </p>
                        {a.details?.field && (
                          <p className="text-[10px] text-zinc-400 mt-0.5">
                            {a.details.field}: <span className="font-mono">{String(a.details.from || '—').slice(0, 40)}</span> → <span className="font-mono">{String(a.details.to || '—').slice(0, 40)}</span>
                          </p>
                        )}
                        <p className="text-[10px] text-zinc-400 mt-0.5">{timeAgo(a.created_at)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Shortcut row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
          <button onClick={() => navigate('/my-tasks')} className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-zinc-200/70 hover:border-blue-300 hover:shadow-sm transition-all text-left group" data-testid="home-shortcut-my-tasks">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center group-hover:scale-105 transition-transform"><CheckCircle size={18} weight="bold" /></div>
            <div className="flex-1 min-w-0"><p className="text-[13px] font-bold text-zinc-800">My tasks</p><p className="text-[11px] text-zinc-500">Everything assigned to you</p></div>
            <ArrowRight size={14} className="text-zinc-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
          </button>
          {['owner', 'admin', 'project_manager'].includes(currentOrg?.role) && (
            <button onClick={() => navigate('/insights')} className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-zinc-200/70 hover:border-violet-300 hover:shadow-sm transition-all text-left group" data-testid="home-shortcut-insights">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white flex items-center justify-center group-hover:scale-105 transition-transform"><ChartLineUp size={18} weight="bold" /></div>
              <div className="flex-1 min-w-0"><p className="text-[13px] font-bold text-zinc-800">Insights</p><p className="text-[11px] text-zinc-500">Velocity, throughput, leaderboard</p></div>
              <ArrowRight size={14} className="text-zinc-400 group-hover:text-violet-600 group-hover:translate-x-0.5 transition-all" />
            </button>
          )}
          <button onClick={() => navigate('/projects')} className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-zinc-200/70 hover:border-emerald-300 hover:shadow-sm transition-all text-left group" data-testid="home-shortcut-projects">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center group-hover:scale-105 transition-transform"><FolderOpen size={18} weight="bold" /></div>
            <div className="flex-1 min-w-0"><p className="text-[13px] font-bold text-zinc-800">All projects</p><p className="text-[11px] text-zinc-500">Jump into any board</p></div>
            <ArrowRight size={14} className="text-zinc-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
          </button>
        </div>
      </div>
      <AppFooter />
    </div>
  );
}
